import { useState, useEffect } from "react";
import { File, Image, Video, X } from "lucide-react";
import api from "../services/api";

const STATUS_STYLE = {
  approved: "bg-green-100 text-green-800",
  pending:  "bg-yellow-100 text-yellow-800",
  rejected: "bg-red-100 text-red-800",
};

const CATEGORY_STYLE = {
  MARKETING:      "bg-purple-100 text-purple-800",
  UTILITY:        "bg-blue-100 text-blue-800",
  AUTHENTICATION: "bg-orange-100 text-orange-800",
};

const TemplatePreviewModal = ({ isOpen, onClose, template }) => {
  const [components, setComponents] = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    if (!isOpen || !template) return;
    setLoading(true);
    setComponents(null);
    api.get(`/message-templates/meta/${encodeURIComponent(template.templateName)}/${encodeURIComponent(template.language)}`)
      .then((r) => setComponents(r.data?.template?.components || []))
      .catch(() => setComponents([]))
      .finally(() => setLoading(false));
  }, [isOpen, template?.templateName, template?.language]);

  if (!isOpen || !template) return null;

  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  // Prefer Meta API components; fall back to DB snapshot fields
  const bodyText = components?.find((c) => c.type === "BODY")?.text
    ?? template.bodyText
    ?? null;
  const footerText = components?.find((c) => c.type === "FOOTER")?.text
    ?? template.footerText
    ?? null;
  const buttons = components?.find((c) => c.type === "BUTTONS")?.buttons || [];

  const hasMedia = ["image", "video", "document"].includes(template.headerType);
  const headerUrl = hasMedia && template.headerValue ? `${baseUrl}${template.headerValue}` : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>

        {/* Header bar */}
        <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
          <button onClick={onClose} className="text-white hover:bg-white/10 rounded-full p-1 transition-colors">
            <X className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-[15px] truncate">{template.templateName}</p>
            <p className="text-white/70 text-[12px]">{template.language?.toUpperCase()}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[template.status?.toLowerCase()] || "bg-gray-100 text-gray-600"}`}>
              {template.status}
            </span>
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${CATEGORY_STYLE[template.category?.toUpperCase()] || "bg-gray-100 text-gray-600"}`}>
              {template.category}
            </span>
          </div>
        </div>

        {/* Chat background */}
        <div className="min-h-[360px] max-h-[560px] overflow-y-auto px-4 py-6 flex items-start justify-end" style={{ backgroundColor: "#efeae2" }}>

          {loading ? (
            <div className="w-full flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-gray-300 border-t-[#075E54] rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-[#d9fdd3] rounded-2xl rounded-tr-none shadow-md max-w-[300px] w-full overflow-hidden border border-[#c6f0c2]">

              {/* Media header */}
              {template.headerType === "image" && (
                headerUrl
                  ? <img src={headerUrl} alt="header" className="w-full max-h-44 object-cover" />
                  : <div className="w-full h-32 bg-gray-100 flex items-center justify-center"><Image className="w-10 h-10 text-gray-300" /></div>
              )}
              {template.headerType === "video" && (
                headerUrl
                  ? <video src={headerUrl} className="w-full max-h-44 bg-black" controls={false} muted preload="metadata" />
                  : <div className="w-full h-32 bg-gray-100 flex items-center justify-center"><Video className="w-10 h-10 text-gray-300" /></div>
              )}
              {template.headerType === "document" && (
                <div className="flex items-center gap-2 bg-black/5 px-3 py-2.5">
                  <File className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <span className="text-xs text-gray-600 truncate">{template.headerValue?.split("/").pop() || "Document"}</span>
                </div>
              )}
              {template.headerType === "text" && template.headerValue && (
                <p className="text-[14px] font-bold text-gray-900 px-3 pt-3 pb-0">{template.headerValue}</p>
              )}

              {/* Body */}
              <div className="px-3 py-2.5">
                {bodyText
                  ? <p className="text-[14px] text-gray-800 whitespace-pre-wrap break-words leading-relaxed">{bodyText}</p>
                  : <p className="text-[13px] text-gray-400 italic">Body text not available.</p>
                }
              </div>

              {/* Footer */}
              {footerText && (
                <p className="text-[12px] text-gray-400 px-3 pb-2">{footerText}</p>
              )}

              {/* Buttons */}
              {buttons.length > 0 && (
                <div className="border-t border-[#c6f0c2]">
                  {buttons.map((btn, i) => (
                    <div key={i} className={`py-2 text-center text-[#007AFF] text-[13px] font-medium ${i < buttons.length - 1 ? "border-b border-[#c6f0c2]" : ""}`}>
                      {btn.text}
                    </div>
                  ))}
                </div>
              )}

              {/* Timestamp */}
              <div className="px-3 pb-2 flex justify-end">
                <span className="text-[11px] text-gray-400">
                  {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
